// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

// Shared chat-shell component — owned by gms/M365-Operations-Agent, fanned byte-identical to
// Audit + Cloud-Adoption via .claude/shared-frontend.json + sync-check.yml. Do not add
// per-agent logic here; pass it through props (labels are i18n strings, the action/footer
// slots take per-agent nodes). See Workstream B.

import { type ReactElement, type ReactNode } from 'react';
import { Card, CardHeader, Avatar, Text } from '@fluentui/react-components';
import { BotRegular } from '@fluentui/react-icons';
import { MarkdownMessage, type MarkdownVariant } from './MarkdownMessage';
import './MessageBubble.css';

interface MessageBubbleProps {
  role: 'user' | 'assistant';
  content: string;
  /** Markdown richness for assistant content (chat = `full`). */
  variant?: MarkdownVariant;
  /** Localised label shown for assistant messages. */
  agentLabel: string;
  /** Localised label shown for user messages. */
  youLabel: string;
  /** Avatar icon for assistant messages (defaults to a bot glyph). */
  agentIcon?: ReactElement;
  /** Assistant header action slot (e.g. a feedback dialog). */
  action?: ReactElement;
  /** Content rendered below the message (e.g. tool-call badges). */
  footer?: ReactNode;
  /** Extra bubble modifier classes (e.g. an intermediate/streaming variant). */
  className?: string;
}

/**
 * Chat message bubble — a Fluent card with an avatar header. Assistant messages render their
 * content through the shared `MarkdownMessage`; user messages render as pre-wrapped text.
 */
export function MessageBubble({
  role,
  content,
  variant = 'full',
  agentLabel,
  youLabel,
  agentIcon = <BotRegular />,
  action,
  footer,
  className,
}: MessageBubbleProps) {
  const isAssistant = role === 'assistant';
  return (
    <Card
      className={`chat-message chat-message--${role}${className ? ` ${className}` : ''}`}
      size="small"
    >
      <CardHeader
        image={
          isAssistant ? (
            <Avatar icon={agentIcon} color="brand" size={28} />
          ) : (
            <Avatar size={28} />
          )
        }
        header={
          <Text weight="semibold" size={200}>
            {isAssistant ? agentLabel : youLabel}
          </Text>
        }
        action={isAssistant ? action : undefined}
      />
      {isAssistant ? (
        <div className="chat-message-content">
          <MarkdownMessage content={content} variant={variant} />
        </div>
      ) : (
        <Text className="chat-message-content" style={{ whiteSpace: 'pre-wrap' }}>
          {content}
        </Text>
      )}
      {footer}
    </Card>
  );
}
